// refund.repo — akses Supabase untuk Pengembalian Dana. Port refundSave/refundVerify v0.18.
import { supabase } from '@core/supabase';
import { rowToTicket, fetchRounds, MONEY_COLS, type TicketRow } from '@core/ticketMap';
import { currentUser } from '@core/session';
import { pushNotif, resolveTaskNotif } from '@core/notif';
import { rp } from '@domain/format';
import type { Ticket, Refund } from '@core/types';

export interface RefundUsul { amount: number; note?: string; by?: string; ronde?: number }
export interface RefundTarget { ticket: Ticket; custNama: string; custWa: string; unit: string; caraBayar: string | null; tglAmbilISO: string | null; refundUsul: RefundUsul | null; }

type CustEmbed = { nama: string; wa?: string } | { nama: string; wa?: string }[] | null;
const nm = (c: CustEmbed): string => (Array.isArray(c) ? c[0]?.nama : c?.nama) ?? '—';
const cwa = (c: CustEmbed): string => (Array.isArray(c) ? c[0]?.wa : c?.wa) ?? '';
const by = (): string => currentUser()?.nama ?? '';

export async function getForRefund(no: string): Promise<RefundTarget> {
  const { data, error } = await supabase().from('tickets')
    .select(`${MONEY_COLS},unit,merk,model,refund_usul,customers(nama,wa)`).eq('no', no).single();
  if (error) throw new Error(error.message);
  const r = data as unknown as TicketRow & { unit: string | null; merk: string | null; model: string | null; refund_usul: RefundUsul | null; customers: CustEmbed };
  const rounds = (await fetchRounds([no])).get(no) ?? [];
  const unit = (r.unit ?? [r.merk, r.model].filter(Boolean).join(' ')) || '—';
  return { ticket: rowToTicket(r, rounds), custNama: nm(r.customers), custWa: cwa(r.customers), unit, caraBayar: r.cara_bayar ?? null, tglAmbilISO: r.tgl_ambil_iso ?? null, refundUsul: r.refund_usul ?? null };
}

/** CS ajukan: simpan refund pending + pindah tiket → Sudah Diambil + log + notif Finance. */
export async function submitRefund(no: string, outlet: string, refund: Refund, pengambil: string, tglAmbilISO: string): Promise<void> {
  const { error } = await supabase().from('tickets')
    .update({ refund, refund_usul: null, pengambil, tgl_ambil_iso: tglAmbilISO, status: 'Sudah Diambil' }).eq('no', no);
  if (error) throw new Error(error.message);
  await supabase().from('ticket_logs').insert({
    ticket_no: no, status: 'Pengembalian Dana',
    catatan: `Refund ${rp(refund.amount)} via ${refund.method ?? '-'} → ${pengambil} (menunggu verifikasi Finance)`, by_user: by(),
  });
  await pushNotif('FINANCE', outlet, `💸 Pengembalian dana ${rp(refund.amount)} pada ${no} menunggu verifikasi.`, no);
}

/** Finance verifikasi: refund.status → verified + log + notif CS. Omzet berkurang otomatis (domain). */
export async function verifyRefund(no: string): Promise<void> {
  const { data, error } = await supabase().from('tickets').select('refund,outlet').eq('no', no).single();
  if (error) throw new Error(error.message);
  const row = data as { refund: Refund | null; outlet: string };
  if (!row.refund || row.refund.status !== 'pending') throw new Error('Refund ini sudah diproses / tidak ada.');
  const verified: Refund = { ...row.refund, status: 'verified', by: row.refund.by };
  const { error: e2 } = await supabase().from('tickets')
    .update({ refund: { ...verified, verifiedBy: by(), verifiedAt: new Date().toISOString() } }).eq('no', no);
  if (e2) throw new Error(e2.message);
  await supabase().from('ticket_logs').insert({
    ticket_no: no, status: 'Pengembalian Terverifikasi',
    catatan: `Refund ${rp(row.refund.amount)} diverifikasi Finance`, by_user: by(),
  });
  await resolveTaskNotif(no, 'FINANCE'); // tugas "menunggu verifikasi refund" selesai → hilang dari lonceng
  await pushNotif('CS', row.outlet, `✅ Refund ${rp(row.refund.amount)} untuk ${no} telah diverifikasi Finance.`, no);
}
